import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PersonalDetails = ({ data, onUpdate, isEditing, setIsEditing }) => {
  const [localData, setLocalData] = useState(data);
  const [errors, setErrors] = useState({});

  const handleEdit = () => {
    setLocalData(data);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setLocalData(data);
    setIsEditing(false);
    setErrors({});
  };

  const handleSave = () => {
    // Basic validation
    const newErrors = {};
    if (!localData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!localData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!localData?.email?.trim()) newErrors.email = 'Email is required';
    if (!localData?.phone?.trim()) newErrors.phone = 'Phone number is required';

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdate(localData);
    setIsEditing(false);
    setErrors({});
  };

  const updateField = (field, value, nested = null) => {
    if (nested) {
      setLocalData(prev => ({
        ...prev,
        [nested]: {
          ...prev?.[nested],
          [field]: value
        }
      }));
    } else {
      setLocalData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Personal Details</h2>
          <p className="text-muted-foreground mt-1">
            Manage your basic information and contact details
          </p>
        </div>
        
        {!isEditing ? (
          <Button
            onClick={handleEdit}
            iconName="Edit"
            variant="outline"
            size="sm"
          >
            Edit
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              onClick={handleCancel}
              variant="ghost"
              size="sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              iconName="Save"
              size="sm"
            >
              Save
            </Button>
          </div>
        )}
      </div>
      {/* Basic Information */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="User" size={20} className="mr-2" />
          Basic Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="First Name"
            required
            value={localData?.firstName || ''}
            onChange={(e) => updateField('firstName', e?.target?.value)}
            error={errors?.firstName}
            disabled={!isEditing}
          />
          
          <Input
            label="Last Name"
            required
            value={localData?.lastName || ''}
            onChange={(e) => updateField('lastName', e?.target?.value)}
            error={errors?.lastName}
            disabled={!isEditing}
          />
          
          <Input
            label="Email Address"
            type="email"
            required
            value={localData?.email || ''}
            onChange={(e) => updateField('email', e?.target?.value)}
            error={errors?.email}
            disabled={!isEditing}
          />
          
          <Input
            label="Phone Number"
            type="tel"
            required
            value={localData?.phone || ''}
            onChange={(e) => updateField('phone', e?.target?.value)}
            error={errors?.phone}
            disabled={!isEditing}
          />
          
          <Input
            label="Date of Birth"
            type="date"
            value={localData?.dateOfBirth || ''}
            onChange={(e) => updateField('dateOfBirth', e?.target?.value)}
            disabled={!isEditing}
          />
          
          <Input
            label="Nationality"
            value={localData?.nationality || ''}
            onChange={(e) => updateField('nationality', e?.target?.value)}
            disabled={!isEditing}
          />
        </div>
      </div>
      {/* Address Information */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="MapPin" size={20} className="mr-2" />
          Address Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Input
              label="Street Address"
              value={localData?.address?.street || ''}
              onChange={(e) => updateField('street', e?.target?.value, 'address')}
              disabled={!isEditing}
            />
          </div>
          
          <Input
            label="City"
            value={localData?.address?.city || ''}
            onChange={(e) => updateField('city', e?.target?.value, 'address')}
            disabled={!isEditing}
          />
          
          <Input
            label="State/Province"
            value={localData?.address?.state || ''}
            onChange={(e) => updateField('state', e?.target?.value, 'address')}
            disabled={!isEditing}
          />
          
          <Input
            label="Postal Code"
            value={localData?.address?.postalCode || ''}
            onChange={(e) => updateField('postalCode', e?.target?.value, 'address')}
            disabled={!isEditing}
          />
          
          <Input
            label="Country"
            value={localData?.address?.country || ''}
            onChange={(e) => updateField('country', e?.target?.value, 'address')}
            disabled={!isEditing}
          />
        </div>
      </div>
      {/* Emergency Contact */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Phone" size={20} className="mr-2" />
          Emergency Contact
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Contact Name"
            value={localData?.emergencyContact?.name || ''}
            onChange={(e) => updateField('name', e?.target?.value, 'emergencyContact')}
            disabled={!isEditing}
          />
          
          <Input
            label="Relationship"
            value={localData?.emergencyContact?.relationship || ''}
            onChange={(e) => updateField('relationship', e?.target?.value, 'emergencyContact')}
            disabled={!isEditing}
          />
          
          <Input
            label="Phone Number"
            type="tel"
            value={localData?.emergencyContact?.phone || ''}
            onChange={(e) => updateField('phone', e?.target?.value, 'emergencyContact')}
            disabled={!isEditing}
          />
          
          <Input
            label="Email Address"
            type="email"
            value={localData?.emergencyContact?.email || ''}
            onChange={(e) => updateField('email', e?.target?.value, 'emergencyContact')}
            disabled={!isEditing}
          />
        </div>
      </div>
      {/* Special Requirements */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Info" size={20} className="mr-2" />
          Special Requirements
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Special Requirements & Accessibility Needs
            </label>
            <textarea
              value={localData?.specialRequirements || ''}
              onChange={(e) => updateField('specialRequirements', e?.target?.value)}
              disabled={!isEditing}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Describe any special requirements, dietary restrictions, accessibility needs, or other important information..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalDetails;